import type { IntlShape } from 'react-intl';

import messages from './messages';
import type { ThreadMeta } from '../types';

const DAY_MS = 86_400_000;

type Formatting = Pick<IntlShape, 'formatMessage' | 'locale'>;

type ThreadGroup = { label: string; items: ThreadMeta[] };

const groupLabel = (
  createdAt: ThreadMeta['createdAt'],
  now: number,
  intl: Formatting
): string => {
  if (!createdAt) return intl.formatMessage(messages.threadsOlder);

  const created = new Date(createdAt);
  const age = now - created.getTime();

  if (age < DAY_MS && created.getDate() === new Date(now).getDate()) {
    return intl.formatMessage(messages.threadsToday);
  }
  if (age < 30 * DAY_MS) {
    return intl.formatMessage(messages.threadsPrevious30Days);
  }
  return created.toLocaleDateString(intl.locale, {
    month: 'long',
    year: 'numeric',
  });
};

export const groupThreads = (
  threads: ThreadMeta[],
  query: string,
  intl: Formatting,
  now = Date.now()
): ThreadGroup[] => {
  const normalizedQuery = query.trim().toLowerCase();
  const visible = normalizedQuery
    ? threads.filter(thread =>
        (thread.name ?? '').toLowerCase().includes(normalizedQuery)
      )
    : threads;

  return visible.reduce<ThreadGroup[]>((groups, thread) => {
    const label = groupLabel(thread.createdAt, now, intl);
    const openGroup = groups.at(-1);
    if (openGroup?.label === label) openGroup.items.push(thread);
    else groups.push({ label, items: [thread] });
    return groups;
  }, []);
};
